/**
 * EntityAvatar — jednotný avatar pro entity/uživatele napříč UI.
 * Zdroj obrázku: entity.metadata.avatar (data URL, nahrává AvatarUpload).
 * Fallback: iniciály na barvě deterministicky odvozené z labelu.
 */
import { useRef, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PALETTE = [
  'bg-blue-600/25 text-blue-300 border-blue-500/30',
  'bg-emerald-600/25 text-emerald-300 border-emerald-500/30',
  'bg-purple-600/25 text-purple-300 border-purple-500/30',
  'bg-amber-600/25 text-amber-300 border-amber-500/30',
  'bg-rose-600/25 text-rose-300 border-rose-500/30',
  'bg-cyan-600/25 text-cyan-300 border-cyan-500/30',
  'bg-indigo-600/25 text-indigo-300 border-indigo-500/30',
  'bg-teal-600/25 text-teal-300 border-teal-500/30',
];

function hashColor(label: string) {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function initials(label: string) {
  const parts = label.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

export interface EntityAvatarProps {
  label: string;
  /** data URL nebo URL obrázku (entity.metadata.avatar) */
  avatar?: string | null;
  /** px — 24 (řádky), 32 (karty), 48+ (detail) */
  size?: number;
  className?: string;
}

export function EntityAvatar({ label, avatar, size = 32, className = '' }: EntityAvatarProps) {
  const fontPx = Math.max(9, Math.round(size * 0.38));
  if (avatar) {
    return (
      <img
        src={avatar}
        alt={label}
        width={size}
        height={size}
        className={`rounded-full object-cover border border-gray-700 shrink-0 ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className={`rounded-full border flex items-center justify-center font-bold shrink-0 select-none ${
        hashColor(label)
      } ${className}`}
      style={{ width: size, height: size, fontSize: fontPx }}
      title={label}
    >
      {initials(label)}
    </div>
  );
}

/** Zmenší obrázek na square JPEG data URL (výchozí 128 px, ~10–25 kB). */
export function resizeImageToDataUrl(file: File, px = 128): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const side = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = px;
      canvas.height = px;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('canvas 2d nedostupný'));
      ctx.drawImage(
        img,
        (img.width - side) / 2,
        (img.height - side) / 2,
        side,
        side,
        0,
        0,
        px,
        px,
      );
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('obrázek nelze načíst'));
    };
    img.src = url;
  });
}

/**
 * AvatarUpload — avatar s hover tužkou; klik otevře file picker,
 * obrázek se zmenší na 128px a předá přes onChange (data URL).
 */
export function AvatarUpload({
  label,
  avatar,
  size = 48,
  onChange,
}: {
  label: string;
  avatar?: string | null;
  size?: number;
  onChange: (dataUrl: string) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    try {
      const dataUrl = await resizeImageToDataUrl(f);
      await onChange(dataUrl);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <button
      type='button'
      onClick={() => inputRef.current?.click()}
      className='relative group rounded-full shrink-0'
      title={t('entity.changeAvatar', 'Změnit avatar')}
    >
      <EntityAvatar label={label} avatar={avatar} size={size} />
      <span className='absolute inset-0 rounded-full bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'>
        {busy
          ? <Loader2 size={Math.round(size * 0.35)} className='animate-spin text-white' />
          : <Pencil size={Math.round(size * 0.35)} className='text-white' />}
      </span>
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        className='hidden'
        onChange={(e) => pick(e.target.files?.[0])}
      />
    </button>
  );
}
